import { useState } from "react";
import { Clock, Edit2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { EscalationTypeBadge } from "@/components/ui/escalation-type-badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ReviewNotice, api, ApiClientError } from "@/lib/api";

interface ReviewNoticePanelProps {
  notice: ReviewNotice;
  onApprove: () => void;
  approving: boolean;
  onUpdate?: (updatedNotice: ReviewNotice) => void;
}

/**
 * ReviewNoticePanel - Modular component for displaying review notice details
 * 
 * Used in the right panel when viewing a review notice.
 * Contains all the notice information and approval controls.
 */
export function ReviewNoticePanel({ notice, onApprove, approving, onUpdate }: ReviewNoticePanelProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(notice.recipient_email);
  const [editingSubject, setEditingSubject] = useState(notice.subject_line);
  const [editingBody, setEditingBody] = useState(notice.message_body);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await api.reviewNotices.update(notice.escalation_step_id, {
        to_email: editingRecipient,
        subject: editingSubject,
        body: editingBody,
      });
      setIsEditing(false);
      if (onUpdate) {
        onUpdate(updated);
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail || "Failed to update notice");
      } else {
        setError("Failed to update notice");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingRecipient(notice.recipient_email);
    setEditingSubject(notice.subject_line);
    setEditingBody(notice.message_body);
    setError(null);
  };
  const getStatusVariant = (status: string): "pending" | "approved" | "sent" => {
    switch (status.toLowerCase()) {
      case "pending":
        return "pending";
      case "in_progress":
        return "approved";
      case "completed":
        return "sent";
      default:
        return "pending";
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status.toLowerCase()) {
      case "pending":
        return "Pending Review";
      case "in_progress":
        return "Admin Approved";
      case "completed":
        return "Sent";
      default:
        return status;
    }
  };

  const formatSendTime = (sendTime: string | null) => {
    if (!sendTime || sendTime === "Immediate") return "Immediate";
    try {
      const date = new Date(sendTime);
      return date.toLocaleString();
    } catch {
      return sendTime;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header Badges */}
      <div className="flex gap-2">
        <StatusBadge status={getStatusVariant(notice.status)}>
          {getStatusLabel(notice.status)}
        </StatusBadge>
        <Badge variant="outline" className="bg-orange-50 text-orange-800 border-orange-200">
          <Clock className="w-3 h-3 mr-1" />
          {formatSendTime(notice.send_time)}
        </Badge>
      </div>

      {/* Case Information */}
      <div className="pb-4 border-b border-gray-200">
        <h4 className="text-sm font-medium text-foreground mb-3">Case Information</h4>
        <div className="space-y-2">
          <div>
            <div className="text-xs text-muted-foreground">Case ID</div>
            <div className="text-sm text-blue-600">{notice.case_number || notice.case_id}</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-muted-foreground">Agency</div>
              <div className="text-sm text-foreground">{notice.agency_name}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Agent</div>
              <div className="text-sm text-foreground">{notice.agent_name}</div>
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Escalation Type</div>
            <EscalationTypeBadge type={notice.escalation_type} className="mt-1" />
          </div>
        </div>
      </div>

      {/* Recipient */}
      <div className="pt-0 pb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-muted-foreground leading-tight">Recipient</label>
          {!isEditing && notice.status.toLowerCase() !== 'completed' && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          )}
        </div>
        {isEditing ? (
          <Input
            value={editingRecipient}
            onChange={(e) => setEditingRecipient(e.target.value)}
            className="text-sm"
          />
        ) : (
          <div className="text-sm text-foreground leading-tight">{notice.recipient_email}</div>
        )}
      </div>

      {/* Subject Line */}
      <div className="pt-0 pb-3">
        <label className="text-xs text-muted-foreground leading-tight mb-1 block">Subject Line</label>
        {isEditing ? (
          <Input
            value={editingSubject}
            onChange={(e) => setEditingSubject(e.target.value)}
            className="text-sm"
          />
        ) : (
          <div className="text-sm text-foreground leading-tight">{notice.subject_line}</div>
        )}
      </div>

      {/* Message Body */}
      <div className="pt-4 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-foreground">Message Body</h4>
        </div>
        {isEditing ? (
          <Textarea
            value={editingBody}
            onChange={(e) => setEditingBody(e.target.value)}
            className="text-sm font-mono min-h-[200px]"
          />
        ) : (
          <div className="bg-muted rounded-lg p-3 text-sm text-foreground whitespace-pre-wrap font-mono text-xs border border-gray-300">
            {notice.message_body}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          {error}
        </div>
      )}

      {/* Edit controls */}
      {isEditing && (
        <div className="flex gap-2 pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1"
            size="sm"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            onClick={handleCancel}
            disabled={saving}
            variant="outline"
            className="flex-1"
            size="sm"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
        </div>
      )}

      {/* Footer Actions */}
      <div className="pt-4">
        <Button 
          className="w-full"
          onClick={onApprove}
          disabled={approving || notice.status.toLowerCase() !== 'pending'}
        >
          {approving ? 'Approving...' : 'Approve Notice'}
        </Button>
      </div>
    </div>
  );
}

